import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useOrderPanelStore, type CustomerData } from '../stores/order-panel-store'
import { OrderComposer } from '../components/order-panel/OrderComposer'
import { PriceSummary } from '../components/order-panel/PriceSummary'
import { ServiceKeuze } from '../components/nieuwe-aanvraag/ServiceKeuze'
import { BookingFlowFrame } from '../components/nieuwe-aanvraag/BookingFlowFrame'
import { KlantStep } from '../components/nieuwe-aanvraag/KlantStep'
import { KlantGegevensForm } from '../components/nieuwe-aanvraag/KlantGegevensForm'
import { AfhandelingBlok, type AfhandelingMode } from '../components/nieuwe-aanvraag/AfhandelingBlok'
import { WederverkoperToggle } from '../components/nieuwe-aanvraag/WederverkoperToggle'
import { BronSelector } from '../components/nieuwe-aanvraag/BronSelector'
import { useUserRole } from '../hooks/useUserRole'
import { isExternLoket } from '../lib/extern-loket'

/**
 * /nieuwe-aanvraag — native office-flow voor het Kleda/vloerkleed-pad,
 * opgebouwd als telefoonscript:
 *
 *   1. Bestaande klant zoeken óf (nieuwe klant) alleen de postcode
 *   2. Kleden samenstellen — prijs direct in beeld om voor te lezen
 *   3. Klant beslist: Direct inboeken / Offerte per e-mail / Afgezien
 *   4. Pas dan de gegevens: alles bij inboeken (adres + planning),
 *      alleen naam/e-mail/telefoon bij offerte of afgezien
 *
 * Overige diensten blijven via de booking-flow iframe lopen.
 */
export function NieuweAanvraagPage() {
  // Extern loket (callcenter) kan de aangemaakte order/offerte niet openen;
  // de links op het succesbeeld blijven dan weg.
  const { role } = useUserRole()
  const loket = isExternLoket(role)
  const [view, setView] = useState<'keuze' | 'kleda' | 'overig'>('keuze')
  // null = klant-stap nog niet voltooid; daarna weten we of het een nieuwe
  // (alleen-postcode) of bestaande (gegevens compleet) klant is.
  const [isNewCustomer, setIsNewCustomer] = useState<boolean | null>(null)
  const [mode, setMode] = useState<AfhandelingMode | null>(null)
  // Walk-in: klant brengt en haalt zelf. Geen postcode gevraagd, geen ritten,
  // adres optioneel — er valt niets te rijden, dus er is geen adres nodig.
  const [walkIn, setWalkIn] = useState(false)

  const customer = useOrderPanelStore((s) => s.customer)
  const initForPage = useOrderPanelStore((s) => s.initForPage)
  const closePanel = useOrderPanelStore((s) => s.closePanel)
  const submitResult = useOrderPanelStore((s) => s.submitResult)
  const channel = useOrderPanelStore((s) => s.channel)

  // Pagina verlaten → store reset zodat de klant-detail-drawer geen stale
  // page-state ziet (openPanel reset sowieso, dit is een extra vangnet).
  useEffect(() => () => closePanel(), [closePanel])

  const started = isNewCustomer !== null && !!customer

  function handleExistingCustomer(c: CustomerData) {
    setIsNewCustomer(false)
    setMode(null)
    setWalkIn(false)
    initForPage(c)
  }

  /**
   * Walk-in: klant staat aan de balie en regelt het transport zelf. Geen
   * postcode, dus prijzen op het wasserij-eigen gebied (geen regiotoeslag) en
   * geen ritten. Adres blijft leeg tenzij kantoor het alsnog invult.
   */
  function handleWalkIn() {
    setIsNewCustomer(true)
    setMode(null)
    setWalkIn(true)
    initForPage(
      { firstName: '', lastName: '', email: '', phone: '', street: '', postcode: '', city: '' },
      { zelfService: true },
    )
  }

  function handleNewPostcode(postcode: string, opts: { country: string; buitenWerkgebied: boolean }) {
    setIsNewCustomer(true)
    setMode(null)
    setWalkIn(false)
    initForPage({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      postcode,
      city: '',
      ...(opts.country !== 'Nederland' ? { country: opts.country } : {}),
      ...(opts.buitenWerkgebied ? { buitenWerkgebied: true } : {}),
    })
  }

  function resetFlow() {
    setIsNewCustomer(null)
    setMode(null)
    setWalkIn(false)
    closePanel()
  }

  const success = submitResult?.ok === true

  // Klantgegevens compleet genoeg om te submitten — per pad. Bestaande
  // klanten zijn altijd compleet; nieuwe klanten vullen het formulier ná de
  // afhandelingskeuze.
  const hasName = !!customer && customer.lastName.trim().length > 0
  const hasEmail = !!customer && customer.email.includes('@')
  const hasPhone = !!customer && customer.phone.trim().length > 0
  const hasAddress = !!customer && customer.street.trim().length > 0 && customer.city.trim().length > 0
  // Een opdracht (ORDER) vereist altijd een telefoonnummer — de chauffeur/planning
  // moet de klant kunnen bellen voor de ophaal/bezorgrit. Voor een nieuwe klant
  // komen naam + adres erbij; een bestaande klant heeft die al, maar het
  // telefoonnummer blijft vereist (zo niet: vul het eerst in op de klantkaart).
  // Afgezien: anonieme registratie — geen gegevens uitvragen, alleen de reden
  // (server zet een herkenbare placeholder-naam; conversiemeting loopt op
  // status + bron, niet op contactgegevens).
  // Walk-in: geen adres vereist — er wordt niets opgehaald of bezorgd. Naam en
  // telefoon blijven wel nodig (de klant moet bereikbaar zijn als er iets is
  // met het kleed).
  const addressReady = walkIn || hasAddress
  const customerReady =
    mode === 'afgezien'
      ? true
      : mode === 'order'
        ? hasPhone && (!isNewCustomer || (hasName && addressReady))
        : mode === 'quote'
          ? (!isNewCustomer || (hasName && hasEmail))
          : !isNewCustomer
  const customerHint =
    mode === 'order' && !hasPhone
      ? isNewCustomer
        ? walkIn
          ? 'Vul naam en telefoonnummer in om in te boeken — een opdracht vereist een telefoonnummer.'
          : 'Vul naam, telefoonnummer en het volledige adres in om in te boeken — een opdracht vereist een telefoonnummer.'
        : 'Deze klant heeft geen telefoonnummer. Vul er eerst één in op de klantkaart — een opdracht vereist een telefoonnummer (een offerte kan wel zonder).'
      : mode === 'order' && isNewCustomer && !addressReady
        ? 'Vul naam en het volledige adres in om in te boeken.'
        : mode === 'order' && isNewCustomer && !hasName
          ? 'Vul de naam van de klant in om in te boeken.'
          : mode === 'quote' && isNewCustomer
            ? 'Vul naam en e-mailadres in — de offerte wordt per e-mail verstuurd.'
            : undefined

  const summaryName = customer
    ? customer.companyName
      ? `${customer.companyName} — ${customer.firstName} ${customer.lastName}`.trim()
      : `${customer.firstName} ${customer.lastName}`.trim()
    : ''

  return (
    <>
      {view === 'overig' ? (
        <BookingFlowFrame onBack={() => setView('keuze')} />
      ) : (
        // Eigen scroll-container — main in AppLayout is overflow-hidden.
        <div className="flex-1 overflow-y-auto bg-slate-50">
          <div className="max-w-5xl mx-auto px-6 py-6">
            {view === 'keuze' && <ServiceKeuze onPick={setView} />}

            {view === 'kleda' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl font-semibold text-slate-900">Vloerkleed reinigen — Kleda</h1>
                    <p className="text-sm text-slate-600">
                      {!started
                        ? 'Zoek de klant — of start met de postcode bij een nieuwe klant'
                        : 'Stel de kleden samen, noem de prijs en kies de afhandeling'}
                    </p>
                  </div>
                  <button
                    onClick={() => { resetFlow(); setView('keuze') }}
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    ← Dienstkeuze
                  </button>
                </div>

                {!started && (
                  <div className="max-w-2xl bg-white border border-slate-200 rounded-xl p-5">
                    <KlantStep
                      onCustomer={handleExistingCustomer}
                      onNewPostcode={handleNewPostcode}
                      onWalkIn={handleWalkIn}
                    />
                  </div>
                )}

                {started && success && (
                  <div className="max-w-2xl bg-white border border-slate-200 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900 mb-1">
                      {mode === 'afgezien'
                        ? 'Aanvraag geregistreerd'
                        : channel === 'ORDER'
                          ? 'Order aangemaakt'
                          : 'Offerte aangemaakt en verstuurd'}
                    </h2>
                    <p className="text-sm text-slate-600 mb-1">
                      {submitResult?.leadnummer && (
                        <span className="font-medium">{submitResult.leadnummer}</span>
                      )}
                      {submitResult?.workOrderNumber && (
                        <span className="font-medium"> · {submitResult.workOrderNumber}</span>
                      )}
                    </p>
                    {mode === 'afgezien' && (
                      <p className="text-sm text-slate-500">
                        Vastgelegd als "niet geïnteresseerd" met reden — de klant ontvangt geen mail.
                      </p>
                    )}
                    {mode === 'quote' && (
                      <p className="text-sm text-slate-500">
                        De klant ontvangt de offerte per e-mail en kiest daar zelf het pakket.
                      </p>
                    )}
                    {submitResult?.capacityBumpWarning && (
                      <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        {submitResult.capacityBumpWarning}
                      </p>
                    )}
                    <div className="mt-5 flex items-center justify-center gap-3">
                      {!loket && submitResult?.workOrderId && (
                        <Link
                          to={`/orders/${submitResult.workOrderId}`}
                          className="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                        >
                          Open order →
                        </Link>
                      )}
                      {!loket && mode === 'quote' && submitResult?.leadId && (
                        <Link
                          to={`/offertes/${submitResult.leadId}`}
                          className="px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                        >
                          Open offerte →
                        </Link>
                      )}
                      <button
                        onClick={resetFlow}
                        className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        Nieuwe aanvraag
                      </button>
                    </div>
                  </div>
                )}

                {started && !success && (
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
                    {/* Linkerkolom: kleden + prijs noemen → afhandeling → gegevens/planning */}
                    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
                      <div className="flex items-center justify-between gap-3">
                        <WederverkoperToggle />
                        <BronSelector />
                      </div>
                      <OrderComposer variant="page" sections="configurator" />
                      <div className="pt-4 border-t border-slate-200">
                        <AfhandelingBlok
                          mode={mode}
                          onModeChange={setMode}
                          customerReady={customerReady}
                          customerHint={customerHint}
                        >
                          {isNewCustomer && (mode === 'order' || mode === 'quote') && (
                            <div className="pt-1">
                              <KlantGegevensForm
                                variant={mode === 'order' ? 'vol' : 'licht'}
                                requireEmail={mode === 'quote'}
                                addressOptional={walkIn}
                              />
                            </div>
                          )}
                          {mode === 'order' && (
                            <div className="space-y-5">
                              <OrderComposer variant="page" sections="planning" />
                            </div>
                          )}
                        </AfhandelingBlok>
                      </div>
                    </div>

                    {/* Rechterkolom: klant + prijs (sticky) */}
                    <div className="space-y-4 lg:sticky lg:top-6">
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Klant</p>
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {summaryName ||
                                (walkIn
                                  ? 'Nieuwe klant — brengt en haalt zelf'
                                  : `Nieuwe klant — ${customer?.postcode ?? ''}`)}
                            </p>
                            {walkIn && (
                              <p className="text-xs text-indigo-600">Zelf brengen en halen — geen ritten</p>
                            )}
                            {(customer?.street || customer?.city) && (
                              <p className="text-xs text-slate-500 truncate">
                                {[customer?.street, customer?.postcode, customer?.city].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {!customer?.street && !customer?.city && summaryName && (
                              <p className="text-xs text-slate-500 truncate">{customer?.postcode}</p>
                            )}
                            {(customer?.phone || customer?.email) && (
                              <p className="text-xs text-slate-500 truncate mt-0.5">
                                {[customer?.phone, customer?.email].filter(Boolean).join(' / ')}
                              </p>
                            )}
                            {isNewCustomer && (
                              <span className="inline-block mt-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                Nieuwe klant
                              </span>
                            )}
                          </div>
                          <button
                            onClick={resetFlow}
                            className="text-xs text-indigo-600 hover:underline flex-shrink-0"
                          >
                            Wijzigen
                          </button>
                        </div>
                      </div>
                      <PriceSummary />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
